/**
 * Render-loop crash guard (Task #227).
 *
 * Cesium's default render loop is fail-CLOSED: if any exception is thrown inside
 * `scene.render()` — most commonly a single undecodable texture
 * ("InvalidStateError: The source image could not be decoded", raised when a
 * billboard-atlas entry or, more often, a high-resolution base-imagery tile
 * fetched at city zoom fails to decode) — the CesiumWidget catches it, sets
 * `useDefaultRenderLoop = false`, and raises `scene.renderError`. The canvas then
 * freezes: the globe goes black and every layer vanishes. One bad image takes
 * down the entire common operating picture.
 *
 * That is never acceptable for an ops tool. This guard re-arms the render loop
 * after such a crash so the COP recovers on the next frame — by which point
 * Cesium has dropped the offending texture/tile (it caches the failure and
 * serves the parent/placeholder), so the bad frame degrades to "no thumbnail"
 * for that one element instead of a black map.
 *
 * To avoid a tight CPU-spinning error storm when a fault is *deterministic*
 * (re-thrown every frame), recoveries are bounded within a rolling window; past
 * the cap the guard stops re-arming and leaves the loop halted, logging loudly.
 * Pair this with `showRenderLoopErrors: false` on the Viewer so Cesium's own
 * full-canvas red error panel doesn't paper over the recovered globe.
 */

/** The slice of the Cesium Viewer/CesiumWidget API this guard touches — kept
 *  structural so it can be unit-tested with a lightweight fake. */
export interface RenderGuardViewer {
  useDefaultRenderLoop: boolean;
  scene: {
    renderError: {
      addEventListener(listener: (scene: unknown, error: unknown) => void): void;
    };
  };
}

export interface RenderGuardOptions {
  /** Max recoveries tolerated within `windowMs` before the guard gives up
   *  re-arming (so a permanent fault can't spin the CPU). Default 12. */
  maxRecoveries?: number;
  /** Rolling window for the recovery budget, in ms. Default 10_000. */
  windowMs?: number;
  /** Defer the re-arm to a later tick (so the throwing frame fully unwinds
   *  first). Default `setTimeout(fn, 0)`. Injectable for tests. */
  schedule?: (fn: () => void) => void;
  /** Clock source. Injectable for tests. Default `Date.now`. */
  now?: () => number;
  /** Sink for diagnostics. Default `console.warn`. Injectable for tests. */
  log?: (message: string, ...rest: unknown[]) => void;
}

/**
 * Install the render-error recovery guard on a Cesium viewer. Returns nothing;
 * the listener lives for the viewer's lifetime (Cesium drops it on destroy).
 */
export function installRenderErrorGuard(
  viewer: RenderGuardViewer,
  options: RenderGuardOptions = {},
): void {
  const maxRecoveries = options.maxRecoveries ?? 12;
  const windowMs = options.windowMs ?? 10_000;
  const schedule = options.schedule ?? ((fn: () => void) => setTimeout(fn, 0));
  const now = options.now ?? (() => Date.now());
  const log = options.log ?? ((msg: string, ...rest: unknown[]) => console.warn(msg, ...rest));

  let recent: number[] = [];

  viewer.scene.renderError.addEventListener((_scene, error) => {
    const t = now();
    recent = recent.filter((ts) => t - ts < windowMs);
    recent.push(t);

    if (recent.length > maxRecoveries) {
      log(
        '[cesiumRenderGuard] render-error storm — leaving render loop halted to avoid a CPU spin; a deterministic fault is throwing every frame',
        { recoveriesInWindow: recent.length, windowMs, error },
      );
      return;
    }

    log(
      '[cesiumRenderGuard] recovered from a Cesium render error so one undecodable image cannot black out the COP',
      error,
    );
    // Re-arm on a later tick: Cesium has already flipped useDefaultRenderLoop to
    // false in this same stack. Setting it back to true restarts the loop; the
    // offending texture/tile is now cached as failed, so the next frame renders.
    schedule(() => {
      viewer.useDefaultRenderLoop = true;
    });
  });
}
